/**
 * Source-of-truth mapping for rendering core.match_candidate.score_components
 * as human-readable match signals in the Review card.
 *
 * The matcher persists a jsonb object of scored components. The canonical
 * orphan-reconciler shape (server/src/lib/match-scoring.ts) is:
 *   { name_sim, street_num_match, street_name_sim, city_match, distance_m }
 * Other matcher paths may emit additional keys (legal/dba similarity, license
 * and chain hints, Place ID linkage), so the map below is a superset. Any key
 * NOT in the map is still rendered — humanized snake_case → Title Case — so we
 * never silently drop a signal.
 */

export type SignalFormat = "score" | "boolean" | "text" | "distance";

export interface SignalLabel {
  label: string;
  format: SignalFormat;
}

export const SIGNAL_LABELS: Record<string, SignalLabel> = {
  name_sim: { label: "Name similarity", format: "score" },
  legal_sim: { label: "Legal entity similarity", format: "score" },
  dba_sim: { label: "DBA similarity", format: "score" },
  trigram_score: { label: "Name trigram", format: "score" },
  street_name_sim: { label: "Street similarity", format: "score" },
  street_sim: { label: "Street similarity", format: "score" },
  street_num_match: { label: "Street number", format: "boolean" },
  address_exact: { label: "Street address", format: "boolean" },
  city_match: { label: "City match", format: "boolean" },
  zip_match: { label: "ZIP match", format: "boolean" },
  distributor_hint: { label: "Distributor hint (C11→C10)", format: "boolean" },
  chain_match: { label: "Chain membership", format: "boolean" },
  place_id_match: { label: "Google Place ID", format: "boolean" },
  distance_m: { label: "Distance", format: "distance" },
};

export type SignalStrength = "strong" | "medium" | "weak";

export interface Signal {
  /** Raw key from score_components. */
  key: string;
  /** Friendly label (mapped or humanized). */
  label: string;
  /** Raw value as stored. */
  value: unknown;
  /** Display string for the value column ("0.65", "exact", "match", "none", …). */
  display: string;
  /**
   * Numeric strength in 0..1 used for sorting and color. Booleans map to 1/0,
   * scores pass through, text/unknown values that aren't numeric sort to 0.
   */
  strength: number;
  /** Bucketed strength for badge color. */
  level: SignalStrength;
  format: SignalFormat;
}

/** snake_case / kebab → Title Case, e.g. "place_id_match" → "Place Id Match". */
export function humanizeKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function inferFormat(value: unknown): SignalFormat {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "score";
  return "text";
}

/** Green ≥ 0.85, yellow 0.5–0.85, gray < 0.5. */
export function strengthLevel(strength: number): SignalStrength {
  if (strength >= 0.85) return "strong";
  if (strength >= 0.5) return "medium";
  return "weak";
}

function formatValue(value: unknown, format: SignalFormat): { display: string; strength: number } {
  switch (format) {
    case "boolean": {
      const truthy = value === true || value === 1;
      return { display: truthy ? "yes" : "no signal", strength: truthy ? 1 : 0 };
    }
    case "distance": {
      if (typeof value === "number") return { display: `${Math.round(value)} m`, strength: 0 };
      return { display: "none", strength: 0 };
    }
    case "score": {
      if (typeof value === "number") {
        // Some "score" fields are really 0/1 flags (e.g. street_num_match).
        return { display: value.toFixed(2), strength: clamp01(value) };
      }
      return { display: "no signal", strength: 0 };
    }
    case "text":
    default: {
      if (value === null || value === undefined || value === "") {
        return { display: "none", strength: 0 };
      }
      if (typeof value === "boolean") return { display: value ? "yes" : "no", strength: value ? 1 : 0 };
      if (typeof value === "number") return { display: String(value), strength: clamp01(value) };
      return { display: String(value), strength: 0 };
    }
  }
}

function clamp01(n: number): number {
  if (Number.isNaN(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * Turn a raw score_components object into sorted, labeled signal rows.
 * Strongest signal first. Never drops a key. Returns [] for null/non-object.
 */
export function deriveSignals(scoreComponents: unknown): Signal[] {
  if (scoreComponents === null || typeof scoreComponents !== "object" || Array.isArray(scoreComponents)) {
    return [];
  }
  const entries = Object.entries(scoreComponents as Record<string, unknown>);
  const signals: Signal[] = entries.map(([key, value]) => {
    const mapped = SIGNAL_LABELS[key];
    const label = mapped?.label ?? humanizeKey(key);
    const format = mapped?.format ?? inferFormat(value);
    const { display, strength } = formatValue(value, format);
    return { key, label, value, display, strength, level: strengthLevel(strength), format };
  });

  // Descending by strength; stable tiebreak by label for deterministic render.
  signals.sort((a, b) => (b.strength - a.strength) || a.label.localeCompare(b.label));
  return signals;
}

/**
 * The strongest NUMERIC (score) signal's strength, or null if there are no
 * numeric/score signals. Boolean flags are excluded — a weak-match warning is
 * about similarity confidence, not the presence of a yes/no hint.
 */
export function topNumericStrength(signals: Signal[]): number | null {
  const numeric = signals.filter((s) => s.format === "score" && typeof s.value === "number");
  if (numeric.length === 0) return null;
  return numeric.reduce((max, s) => Math.max(max, s.strength), 0);
}

/** True when the strongest numeric signal exists and is below 0.5. */
export function isWeakMatch(signals: Signal[]): boolean {
  const top = topNumericStrength(signals);
  return top !== null && top < 0.5;
}
